import {
  ComparingEnum,
  DashboardCardNameEnum,
  DashboardCardTemplateTypeEnum,
  DashboardGraphColorMappingEnum,
} from "../../enums";
import {
  numericContentCardComparingColoringIndicator,
  twoPointsAfterDecimal,
} from "../../helpers";
import {
  IBarGraphContent,
  IBarGraphSettings,
  INumericComparisonCardContent,
  INumericComparisonCardSettings,
  IPieGraphContent,
  IPieGraphContentArc,
  IPieGraphSettings,
} from "./dashboard-defs";
import { ICardSettings } from "./dashboard.card.settings.dtos";
import { IColorPredicateOptions } from "./dashboard.color.helpers";

const noneValue = "None";
export const CardNameToTypeMap: Record<
  DashboardCardNameEnum,
  DashboardCardTemplateTypeEnum
> = {
  [DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_OVERALL]:
    DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON,
  [DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_PER_USER]:
    DashboardCardTemplateTypeEnum.HORIZONTAL_BARS,
  [DashboardCardNameEnum.STATUS_NEW_BOOKINGS]:
    DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON,
  [DashboardCardNameEnum.OVERALL_COST]:
    DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON,
  [DashboardCardNameEnum.VEHICLE_TYPE_RIDES_COST]:
    DashboardCardTemplateTypeEnum.PIE_CHART,
  [DashboardCardNameEnum.RIDES_SUPPLIERS]:
    DashboardCardTemplateTypeEnum.VERTICAL_BARS,
  [DashboardCardNameEnum.PASSENGERS_WITHOUT_SITE]:
    DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON,
  [DashboardCardNameEnum.OPEN_FEEDBACKS]:
    DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON,
  [DashboardCardNameEnum.PRICE_RATES_ZERO]:
    DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON,
  [DashboardCardNameEnum.NONE]: DashboardCardTemplateTypeEnum.NONE,
};

export type CardTypeToContentMap = {
  [DashboardCardTemplateTypeEnum.PIE_CHART]: IPieGraphContent;
  [DashboardCardTemplateTypeEnum.HORIZONTAL_BARS]: IBarGraphContent;
  [DashboardCardTemplateTypeEnum.VERTICAL_BARS]: IBarGraphContent;
  [DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON]: INumericComparisonCardContent;
  [DashboardCardTemplateTypeEnum.NONE]: undefined;
};
export type CardTypeToSettingsMap = {
  [DashboardCardTemplateTypeEnum.PIE_CHART]: IPieGraphSettings;
  [DashboardCardTemplateTypeEnum.HORIZONTAL_BARS]: IBarGraphSettings;
  [DashboardCardTemplateTypeEnum.VERTICAL_BARS]: IBarGraphSettings;
  [DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON]: INumericComparisonCardSettings;
  [DashboardCardTemplateTypeEnum.NONE]: undefined;
};
export const CardNameToColorSchemeMap: Record<
  DashboardCardNameEnum,
  DashboardGraphColorMappingEnum
> = {
  [DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_PER_USER]:
    DashboardGraphColorMappingEnum.SINGLE_COLOR,
  [DashboardCardNameEnum.VEHICLE_TYPE_RIDES_COST]:
    DashboardGraphColorMappingEnum.DIFFERENT_COLORS_WITH_CONDITION,
  [DashboardCardNameEnum.RIDES_SUPPLIERS]:
    DashboardGraphColorMappingEnum.TWO_COLORS_WITH_CONDITION,
  [DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_OVERALL]:
    DashboardGraphColorMappingEnum.NONE,
  [DashboardCardNameEnum.STATUS_NEW_BOOKINGS]:
    DashboardGraphColorMappingEnum.NONE,
  [DashboardCardNameEnum.OVERALL_COST]: DashboardGraphColorMappingEnum.NONE,
  [DashboardCardNameEnum.PASSENGERS_WITHOUT_SITE]:
    DashboardGraphColorMappingEnum.NONE,
  [DashboardCardNameEnum.OPEN_FEEDBACKS]: DashboardGraphColorMappingEnum.NONE,
  [DashboardCardNameEnum.PRICE_RATES_ZERO]: DashboardGraphColorMappingEnum.NONE,
  [DashboardCardNameEnum.NONE]: DashboardGraphColorMappingEnum.NONE,
};

export const CardNameToFieldInAPI: Record<DashboardCardNameEnum, string> = {
  [DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_PER_USER]:
    "ridePageManualChangesPerUser",
  [DashboardCardNameEnum.VEHICLE_TYPE_RIDES_COST]: "vehicleTypeRidesCost",
  [DashboardCardNameEnum.RIDES_SUPPLIERS]: "ridesSuppliers",
  [DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_OVERALL]:
    "ridePageManualChangesOverall",
  [DashboardCardNameEnum.STATUS_NEW_BOOKINGS]: "statusNewBooking",
  [DashboardCardNameEnum.OVERALL_COST]: "overallCost",
  [DashboardCardNameEnum.PASSENGERS_WITHOUT_SITE]: "passengersWithoutSite",
  [DashboardCardNameEnum.OPEN_FEEDBACKS]: "openFeedbacks",
  [DashboardCardNameEnum.PRICE_RATES_ZERO]: "priceRatesZero",
  [DashboardCardNameEnum.NONE]: "NONE",
};

export const CardNameToOptionsMap: Record<
  DashboardCardNameEnum,
  ICardSettings
> = {
  [DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_OVERALL]: {
    titleCardSettings: {
      entity: "ride",
    },
    mainCardSettings: {
      maxDiffPercent: -1,
      predicate: (valueToday, valueLastWeek, maxDiffPercent) => {
        if (!valueLastWeek) {
          return ComparingEnum.NONE;
        }

        return ComparingEnum.GOOD;
      },
      // TODO: fix explicit definition of object
      dataHook: (obj: { ridesToday: number; ridesLastWeek: number }) => {
        return {
          valueToday: obj.ridesToday,
          valueLastWeek: obj.ridesLastWeek,
        };
      },
      hasSubheader: true,
    },
  },
  [DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_PER_USER]: {
    titleCardSettings: { entity: "ride" },
    mainCardSettings: {
      fieldValueToGetSecondaryValue: "subLabel",
      maxDisplay: 5,
      predicateFunctionColor: _ => {
        return true;
      },
      // TODO: fix explicit definition of object
      dataHook: (elems: { numRides: number; userName?: string | null }[]) => {
        const mapped = elems.map(ele => {
          if (
            ele.userName === null ||
            ele.userName === undefined ||
            ele.userName === noneValue
          ) {
            return { subLabel: "", value: -1 };
          }

          return { subLabel: ele.userName, value: ele.numRides };
        });

        return { entities: mapped };
      },
      sorter: (content: IBarGraphContent, maxDisplay: number) => {
        const entities = content.entities
          .filter(ele => ele.subLabel !== "")
          .slice()
          .sort((a, b) => b.value - a.value);

        if (entities.length < maxDisplay) {
          return { entities: entities };
        }

        return { entities: entities.slice(0, maxDisplay) };
      },
      preferredColorNumberForMainColor: 2,
    },
  },
  [DashboardCardNameEnum.STATUS_NEW_BOOKINGS]: {
    titleCardSettings: { entity: "booking" },
    mainCardSettings: {
      maxDiffPercent: 0,
      predicate: _ => {
        return ComparingEnum.NONE;
      },
      dataHook: (obj: { newBookingToday: number }) => {
        return {
          valueToday: obj.newBookingToday,
        };
      },
      hasSubheader: false,
    },
  },
  [DashboardCardNameEnum.OVERALL_COST]: {
    titleCardSettings: { entity: "ride" },
    mainCardSettings: {
      maxDiffPercent: 25,
      predicate: (valueToday, valueLastWeek, maxDiffPercent) => {
        if (!valueLastWeek) {
          return ComparingEnum.NONE;
        }

        return numericContentCardComparingColoringIndicator(
          valueToday,
          valueLastWeek,
          maxDiffPercent ?? 0,
        )
          ? ComparingEnum.BAD
          : ComparingEnum.GOOD;
      },
      // TODO: fix explicit definition of object
      dataHook: (obj: {
        totalPriceToday: number;
        totalPriceLastWeek: number;
      }) => {
        return {
          valueToday: twoPointsAfterDecimal(obj.totalPriceToday),
          valueLastWeek: twoPointsAfterDecimal(obj.totalPriceLastWeek),
        };
      },
      hasSubheader: true,
    },
  },
  [DashboardCardNameEnum.VEHICLE_TYPE_RIDES_COST]: {
    titleCardSettings: { entity: "ride" },
    mainCardSettings: {
      labelsFormat: (arc: IPieGraphContentArc) =>
        `${arc.params.vehicleTypeName}\n${arc.params.supplier}\n${arc.params.percentOfTotalRides} - ${arc.params.totalPrice}`,
      fieldValueToGetSecondaryValue: 4,
      fieldName: "maxPassengers",
      predicateFunctionColor: (
        colorPredicateOptions: IColorPredicateOptions,
      ): boolean => {
        const { objectToCheck, fieldToCheck, fieldValueToCheck } =
          colorPredicateOptions;
        return objectToCheck[fieldToCheck] <= fieldValueToCheck;
      },
      // TODO: fix explicit definition of object
      dataHook: (
        elems: {
          supplier: string;
          vehicleTypeName: string;
          totalNumberOfRides: number;
          totalPrice: number;
          maxPassengers: number;
        }[],
      ) => {
        const sumRides = elems
          .map(ele => Number(ele.totalNumberOfRides))
          .reduce((sum, cur) => sum + cur, 0);

        return {
          arcs: elems.map(ele => {
            return {
              value: ele.totalNumberOfRides,
              params: {
                supplier: ele.supplier,
                vehicleTypeName: ele.vehicleTypeName,
                totalNumberOfRides: ele.totalNumberOfRides,
                totalPrice: twoPointsAfterDecimal(Number(ele.totalPrice)),
                maxPassengers: ele.maxPassengers,
                percentOfTotalRides:
                  Math.round(
                    (Number(ele.totalNumberOfRides) / sumRides) * 100,
                  ) + "%",
              },
            };
          }),
        };
      },
      legendFieldsFromParams: ["vehicleTypeName", "supplier"],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      legendDataHook: (arcsParams: any[], fields: string[]) => {
        // todo: fix any
        return arcsParams.map(elem => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const obj = {} as any;
          fields.forEach(field => {
            obj[field] = elem["params"][field];
          });
          return obj;
        });
      },
    },
  },
  [DashboardCardNameEnum.RIDES_SUPPLIERS]: {
    titleCardSettings: { entity: "ride" },
    mainCardSettings: {
      maxDisplay: -1,
      fieldValueToGetSecondaryValue: noneValue,
      predicateFunctionColor: (
        colorPredicateOptions: IColorPredicateOptions,
      ): boolean => {
        const { objectToCheck, fieldToCheck, fieldValueToCheck } =
          colorPredicateOptions;
        return objectToCheck[fieldToCheck] === fieldValueToCheck;
      },
      // TODO: fix explicit definition of object
      dataHook: (elems: { numRides: number; supplierName: string }[]) => {
        const ents = elems.map(ele => {
          return { subLabel: ele.supplierName, value: ele.numRides };
        });
        return { entities: ents };
      },
      sorter: (content: IBarGraphContent, _: number) => {
        const [noneBar, restBars] = [
          content.entities.filter(ele => ele.subLabel === noneValue),
          content.entities.filter(ele => ele.subLabel !== noneValue),
        ];
        const sorted = restBars.slice().sort((a, b) => b.value - a.value);

        if (
          noneBar.length === 0 ||
          (noneBar.length === 1 && noneBar[0].value === 0)
        ) {
          return { entities: sorted };
        } else {
          return { entities: [...sorted, ...noneBar] };
        }
      },
      preferredColorNumberForMainColor: 1,
    },
  },

  [DashboardCardNameEnum.PASSENGERS_WITHOUT_SITE]: {
    titleCardSettings: { entity: "passenger" },
    mainCardSettings: {
      maxDiffPercent: 0,
      predicate: _ => {
        return ComparingEnum.NONE;
      },
      dataHook: (obj: { count: number }) => {
        return { valueToday: obj.count };
      },
      hasSubheader: false,
    },
  },
  [DashboardCardNameEnum.OPEN_FEEDBACKS]: {
    titleCardSettings: { entity: "feedback" },
    mainCardSettings: {
      maxDiffPercent: 0,
      predicate: _ => {
        return ComparingEnum.NONE;
      },
      dataHook: (obj: { count: number }) => {
        return { valueToday: obj.count };
      },
      hasSubheader: false,
    },
  },
  [DashboardCardNameEnum.PRICE_RATES_ZERO]: {
    titleCardSettings: { entity: "priceRate" },
    mainCardSettings: {
      maxDiffPercent: 0,
      predicate: _ => {
        return ComparingEnum.NONE;
      },
      dataHook: (obj: { count: number }) => {
        return { valueToday: obj.count };
      },
      hasSubheader: false,
    },
  },
  [DashboardCardNameEnum.NONE]: {
    titleCardSettings: {},
    mainCardSettings: {
      maxDiffPercent: 0,
      predicate: _ => {
        return ComparingEnum.NONE;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dataHook: (_: any) => {
        return {
          valueToday: -1,
          valueLastWeek: -1,
        };
      },
      hasSubheader: false,
    },
  },
};
